import { confirmBox } from '../components/dialog.js';
import { renderHeader } from '../components/header.js';
import { renderNav } from '../components/nav.js';
import { loadSettings, saveSettings, loadGoals, upsertGoal, setRaceDate, exportAll, shortcutKeyInfo, makeShortcutKey } from '../lib/grit-data.js';
import { activeUser, label, logout } from '../lib/auth.js';
import { go } from '../router.js';
import { toast } from '../components/toast.js';
import { enablePush, disablePush } from '../lib/push.js';
import { icon } from '../components/icons.js';
import { openPlanChooser } from '../components/plan-chooser.js';
import { phaseInfo, phaseLabel, localISO, PHASE_WEEKS } from '../lib/phase.js';

const DEFAULTS = { rest_default: 180, sound: true, vibration: true, notifications: false, out_by: '' };

const secToMmss = (sec) => `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, '0')}`;
function mmssToSec(str) {
  const m = String(str).trim().match(/^(\d+):(\d{1,2})$/);
  if (m) return (+m[1]) * 60 + (+m[2]);
  const n = Number(str);
  return Number.isFinite(n) ? n : null;
}

// Mirror sound/vibration prefs to localStorage so the rest timer can read them synchronously.
export function cachePrefs(s) {
  localStorage.setItem('grit-pref-sound', s.sound === false ? '0' : '1');
  localStorage.setItem('grit-pref-vibration', s.vibration === false ? '0' : '1');
  localStorage.setItem('grit-pref-outby', s.out_by || '');
}

// iOS-style switch (51 x 31)
function toggle(id, on, name) {
  return `<button data-toggle="${id}" role="switch" aria-checked="${on}" aria-label="${name}" class="relative w-[51px] h-[31px] rounded-full flex-none transition-colors duration-200 ${on ? 'bg-success' : 'bg-[rgba(120,120,128,0.24)]'}">
    <span class="absolute top-[2px] ${on ? 'left-[22px]' : 'left-[2px]'} w-[27px] h-[27px] rounded-full bg-white transition-all duration-200" style="box-shadow:0 3px 8px rgba(0,0,0,0.15),0 1px 1px rgba(0,0,0,0.16)"></span>
  </button>`;
}
// Grouped-list row (Apple Settings): label left, control right, hairline between rows.
function row(labelText, control, sub) {
  return `<div class="flex items-center justify-between gap-3 px-4 min-h-[52px] py-2 border-t border-hairline first:border-0">
    <div class="min-w-0"><div class="text-[17px] text-ink">${labelText}</div>${sub ? `<div class="text-[13px] text-inksoft">${sub}</div>` : ''}</div>
    ${control}
  </div>`;
}
const group = (title, body, foot = '') => `
  ${title ? `<div class="text-[13px] text-inksoft px-4 mb-1.5">${title}</div>` : ''}
  <div class="grit-card overflow-hidden">${body}</div>
  ${foot ? `<div class="text-[13px] text-inksoft px-4 mt-1.5">${foot}</div>` : ''}
  <div class="h-7"></div>`;

export function renderSettings(el) {
  const me = activeUser();
  el.innerHTML = `${renderHeader()}<div class="grit-page px-5 pt-4"><p class="text-inksoft">Loading…</p></div>${renderNav('settings')}`;

  (async () => {
    const [stored, goals, keyInfo] = await Promise.all([loadSettings(me), loadGoals(me), shortcutKeyInfo()]);
    let freshKey = null; // shown once, right after it is made
    const s = { ...DEFAULTS, ...stored };
    cachePrefs(s);
    let race = goals.find((g) => g.kind === 'race_10k') || null;
    const mile = goals.find((g) => g.kind === 'mile_time');

    async function patch(p) {
      Object.assign(s, p);
      cachePrefs(s);
      // Persist the full in-memory state so two quick toggles can't race-clobber each other.
      const ok = await saveSettings({ ...s });
      if (!ok) toast("Couldn't save, try again", { tone: 'bad' });
      paint();
    }

    function paint() {
      const ph = phaseInfo(s.phase_start, localISO());
      const inputCls = 'grit-input !w-auto !min-h-[44px] !py-2 !px-3 text-right flex-none';
      el.innerHTML = `
        ${renderHeader()}
        <div class="grit-page px-5 pt-4">
          <div class="grit-eyebrow">${label(me)}</div>
          <h1 class="grit-title mt-0.5 mb-5">Settings</h1>

          ${group('Training', `
            <button id="choose-plan" class="grit-row w-full flex items-center justify-between px-4 min-h-[52px] text-left">
              <span class="text-[17px] text-ink">Choose my plan</span><span class="flex items-center gap-1 text-inksoft"><span class="text-[15px]">Days, routine</span>${icon.right('w-4 h-4')}</span>
            </button>
            <button id="open-editor" class="grit-row w-full flex items-center justify-between px-4 min-h-[52px] text-left border-t border-hairline">
              <span class="text-[17px] text-ink">Edit my program</span><span class="text-inksoft">${icon.right('w-4 h-4')}</span>
            </button>
            ${row('Training phase', `<span class="text-[17px] text-inksoft flex-none">${ph ? phaseLabel(ph) : 'Starts with your next workout'}</span>`)}
            ${ph ? `<button id="new-phase" class="grit-row w-full text-left px-4 min-h-[52px] text-[17px] text-grit border-t border-hairline">Start a new phase today</button>` : ''}
          `, `The book trains in ${PHASE_WEEKS}-week phases: 8 hard weeks, then a deload week (p.314, 332).`)}

          ${group('Rest timer', `
            ${row('Default rest', `<div class="flex items-center gap-1 flex-none">
              <button data-rest="-15" aria-label="Less rest" class="w-11 h-11 rounded-full bg-fill text-ink text-[20px] font-semibold active:scale-95 transition-transform">–</button>
              <span class="text-[17px] text-ink w-14 text-center tabular-nums">${secToMmss(s.rest_default)}</span>
              <button data-rest="15" aria-label="More rest" class="w-11 h-11 rounded-full bg-fill text-ink text-[20px] font-semibold active:scale-95 transition-transform">+</button>
            </div>`)}
            ${row('Sound', toggle('sound', s.sound !== false, 'Timer sound'))}
            ${row('Vibration', toggle('vibration', s.vibration !== false, 'Vibration'))}
            ${row('Out-by time', `<input id="out-by" type="time" value="${s.out_by || ''}" aria-label="Out-by time" class="${inputCls}" />`)}
          `, 'Out-by time shows a live finish estimate while you lift. The book rests about 3 minutes on big lifts, 2 on the rest (p.252).')}

          ${group('Notifications', row('Sunday focus reminder', toggle('notifications', !!s.notifications, 'Sunday focus reminder')),
            "One nudge on Sunday morning to set the week's focus, only if you don't have one going. On iPhone, add Grit to your Home Screen first.")}

          ${group('Running', `
            ${row('Race date', `<input id="race-date" type="date" value="${race?.target_date || ''}" aria-label="Race date" class="${inputCls}" />`)}
            ${row('10K goal', `<div class="flex items-center gap-1.5 flex-none"><input id="race-target" type="number" inputmode="numeric" value="${race?.target || ''}" placeholder="--" aria-label="10K goal in minutes" class="${inputCls}" style="width:84px" /><span class="text-[15px] text-inksoft">min</span></div>`)}
            ${mile ? row('Mile goal', `<input id="mile-target" type="text" inputmode="numeric" value="${secToMmss(Number(mile.target || 450))}" aria-label="Mile goal, minutes and seconds" class="${inputCls}" style="width:96px" />`) : ''}
          `, 'With a race ahead, running builds up to it. With none, you get steady weeks that follow the book\'s cardio rules.')}

          ${group('Phone shortcuts', freshKey
            ? `<div class="px-4 py-3">
                <div class="text-[15px] text-ink font-semibold">Your shortcut key</div>
                <div id="key-text" class="mt-2 p-3 rounded-xl bg-pagebg text-[13px] text-ink break-all select-all" style="font-family:ui-monospace,Menlo,monospace">${freshKey}</div>
                <button id="copy-key" class="grit-btn mt-3">Copy key</button>
              </div>`
            : row(keyInfo?.hasKey ? 'Shortcut key is set' : 'Shortcut key', `<button id="make-key" class="grit-pill flex-none">${keyInfo?.hasKey ? 'Make a new one' : 'Make my key'}</button>`,
                keyInfo?.hasKey ? `Made ${new Date(keyInfo.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''),
            freshKey ? 'Paste it into the Grit shortcuts on your iPhone now. It is shown only once; making a new key turns this one off.' : 'Lets the lock-screen calorie button, the scale and MyFitnessPal send to Grit. Setup steps are in the Grit folder (phone/SETUP.md).')}

          ${group('', `
            <button id="export" class="grit-row w-full text-left px-4 min-h-[52px] text-[17px] text-grit">Export all my data</button>
          `, 'One file with your program, workouts, runs, food, weigh-ins and settings. Keep a copy somewhere safe.')}

          ${group('', `<button id="signout" class="grit-row w-full text-left px-4 min-h-[52px] text-[17px] text-danger">Sign out</button>`)}
          <div class="text-center text-[13px] text-inksoft">Grit · Train. Track. Transform.</div>
        </div>
        ${renderNav('settings')}`;

      el.querySelectorAll('[data-toggle]').forEach((b) => {
        b.onclick = async () => {
          const key = b.dataset.toggle;
          const next = !s[key];
          if (key === 'notifications') {
            if (next) {
              const { ok, reason } = await enablePush();
              if (!ok) {
                const msg = {
                  'ios-needs-install': 'To get reminders on iPhone, add Grit to your Home Screen first. Tap the Share button, then "Add to Home Screen," and open Grit from there.',
                  'denied': 'Notifications are turned off for Grit. Turn them back on in your phone Settings, then try again.',
                  'subscribe-failed': "Couldn't turn on reminders. Check your connection and try again.",
                  'unsupported': "This device can't show reminders.",
                }[reason] || "This device can't show reminders.";
                toast(msg, { tone: 'bad' });
                paint();
                return;
              }
              toast('Reminders on');
            } else {
              await disablePush();
            }
          }
          patch({ [key]: next });
        };
      });
      el.querySelectorAll('[data-rest]').forEach((b) => {
        b.onclick = () => patch({ rest_default: Math.max(30, Math.min(360, (s.rest_default || 180) + Number(b.dataset.rest))) });
      });
      const ob = document.getElementById('out-by');
      if (ob) ob.onchange = () => patch({ out_by: ob.value });
      const np = document.getElementById('new-phase');
      if (np) np.onclick = async () => {
        if (!(await confirmBox({ title: 'Start a new phase today?', message: 'Week 1 starts this week. Do it after a deload week.', confirmLabel: 'Start' }))) return;
        patch({ phase_start: localISO() });
      };
      const rd = document.getElementById('race-date');
      if (rd) rd.onchange = async () => {
        const ok = !(await upsertGoal({ kind: 'race_10k', current: race?.current || null, target: race?.target || null, target_date: rd.value || null })).error;
        const ok2 = await setRaceDate(rd.value || null);
        race = { ...(race || {}), kind: 'race_10k', target_date: rd.value || null };
        toast(ok && ok2 ? 'Saved' : "Couldn't save, try again", { tone: ok && ok2 ? 'good' : 'bad' });
      };
      const rt = document.getElementById('race-target');
      if (rt) rt.onchange = async () => { await upsertGoal({ kind: 'race_10k', current: race?.current || null, target: rt.value || null, target_date: race?.target_date || null }); race = { ...(race || {}), target: rt.value }; toast('Saved'); };
      const mt = document.getElementById('mile-target');
      if (mt) mt.onchange = async () => { const sec = mmssToSec(mt.value); if (sec == null) { toast('Use minutes:seconds, like 7:30', { tone: 'bad' }); return; } await upsertGoal({ kind: 'mile_time', current: mile?.current || null, target: String(sec) }); if (mile) mile.target = String(sec); toast('Saved'); };
      document.getElementById('open-editor').onclick = () => go('program-editor');
      const mk = document.getElementById('make-key');
      if (mk) mk.onclick = async () => {
        if (keyInfo?.hasKey && !(await confirmBox({ title: 'Make a new shortcut key?', message: 'Your shortcuts stop working until you paste the new key into them.', confirmLabel: 'Make new key' }))) return;
        const k = await makeShortcutKey();
        if (!k) { toast("Couldn't make a key, try again", { tone: 'bad' }); return; }
        freshKey = k; keyInfo.hasKey = true; keyInfo.createdAt = new Date().toISOString(); paint();
      };
      const ck = document.getElementById('copy-key');
      if (ck) ck.onclick = async () => { try { await navigator.clipboard.writeText(freshKey); toast('Key copied'); } catch { toast('Press and hold the key to copy it', { tone: 'info' }); } };
      document.getElementById('choose-plan').onclick = () => openPlanChooser({ onApplied: () => go('home') });
      document.getElementById('export').onclick = async () => {
        toast('Building your export', { tone: 'info' });
        const data = await exportAll(me);
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `grit-${me}-${localISO()}.json`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(a.href);
        toast('Exported');
      };
      document.getElementById('signout').onclick = async () => { if (await confirmBox({ title: 'Sign out?', confirmLabel: 'Sign out', danger: true })) { logout(); location.hash = ''; location.reload(); } };
    }

    paint();
  })();
}
